import { create } from 'zustand';
import { type StateStorage, createJSONStorage, persist } from 'zustand/middleware';
import { isTerminalPhase } from '../lib/uploadLifecycle';
import {
  migratePersistedUploadTasks,
  retainPersistedUploadTasks,
  serializePersistedUploadTasks,
} from '../lib/uploadPersistence';
import { transitionUploadTask } from '../lib/uploadTaskMachine';
import { type UploadTaskPatch, applyUploadTaskPatches } from '../lib/uploadTaskPatches';
import {
  setUploadTabHasActiveUploads,
  startUploadTabCoordination,
  stopUploadTabCoordination,
} from '../services/leaderCoordinator';
import { uploadFileHandleStore } from '../services/uploadFileHandleStore';
import { uploadRuntimeClient } from '../services/uploadRuntimeClient';
import type { UploadPhase, UploadTask } from '../types/uploads';
import { isLocalUploadLeader, useUploadLeadershipStore } from './uploadLeadershipStore';
import { uploadPreparationActions } from './uploadPreparationStore';
import { uploadProgressActions, useUploadProgressStore } from './uploadProgressStore';

export { TERMINAL_PHASES, isTerminalPhase } from '../lib/uploadLifecycle';

/**
 * The upload queue: one {@link UploadTask} per upload/import, driven by the imperative
 * upload engine (`services/uploadEngine`) and the status-poll services. Lives in a module-scoped Zustand
 * store (per the "stores live in a `stores/` segment" rule) so uploads survive route changes and the
 * `/transfers` page can subscribe to the same source whenever it is mounted. The engine holds the actual XHR
 * / AbortController refs outside the store (they are not serializable). High-frequency uncommitted
 * byte progress also stays outside this persisted store in `uploadProgressStore`.
 *
 * The store is `persist`-wrapped to `localStorage` so the queue survives a full page reload. Only
 * serializable render/identity metadata is persisted (see `partialize`) — never a `File` or an
 * `XMLHttpRequest`; those live in the engine's in-memory map. The SharedWorker keeps a second live
 * File reference for Leader handoff, while IndexedDB may hold a FileSystemFileHandle when selection
 * came from the File System Access API. On rehydrate, {@link markNeedsReattach} first marks byte
 * phases as interrupted; recovery then clears the flag only after a retained File/handle passes the
 * normal identity and backend-offset checks. Post-upload processing needs no local File.
 */

/**
 * Upload phases that still need access to the browser-local `File`. `completing` is deliberately
 * excluded because GCS may already have every byte and the backend can finish without local data;
 * `processing` is wholly server-side. Both are reconciled against the backend after hydration.
 * (`error` is handled separately because only upload-operation errors need reattachment.)
 */
const FILE_DEPENDENT_UPLOAD_PHASES: ReadonlySet<UploadPhase> = new Set<UploadPhase>([
  'queued',
  'reattaching',
  'hashing',
  'prechecking',
  'creating',
  'uploading',
  'paused',
  'transferring',
]);

/** Keep the develop-era key so unfinished uploads survive the feature-directory rename. */
const PERSIST_KEY = 'vl-transfers';
const PERSIST_VERSION = 2;
let activatedUid: string | null = null;
let storageUid: string | null = null;
let activationGeneration = 0;
let unsubscribeLeadership: (() => void) | null = null;
let runtimeResetPromise: Promise<void> | null = null;
let sharedRuntimeAvailable = false;
let suppressRuntimePublish = false;

const uidStorage: StateStorage = {
  getItem: (name) => (storageUid ? localStorage.getItem(`${name}:${storageUid}`) : null),
  setItem: (name, value) => {
    if (storageUid && isLocalUploadLeader()) localStorage.setItem(`${name}:${storageUid}`, value);
  },
  removeItem: (name) => {
    if (storageUid && isLocalUploadLeader()) localStorage.removeItem(`${name}:${storageUid}`);
  },
};

interface UploadStoreState {
  items: UploadTask[];
  add: (item: UploadTask) => void;
  patch: (id: string, patch: Partial<UploadTask>) => void;
  patchMany: (patches: readonly UploadTaskPatch[]) => void;
  remove: (id: string) => void;
  clearCompleted: () => void;
}

interface PersistedUploadStoreState {
  items: UploadTask[];
}

function persistedItemsFromUnknown(state: unknown): UploadTask[] {
  if (!state || typeof state !== 'object') return [];
  const items = (state as { items?: unknown }).items;
  return Array.isArray(items) ? retainPersistedUploadTasks(items as UploadTask[]) : [];
}

function migratedItemsFromUnknown(state: unknown): UploadTask[] {
  if (!state || typeof state !== 'object') return [];
  const items = (state as { items?: unknown }).items;
  return Array.isArray(items) ? migratePersistedUploadTasks(items as UploadTask[]) : [];
}

export const useUploadStore = create<UploadStoreState>()(
  persist<UploadStoreState, [], [], PersistedUploadStoreState>(
    (set, get) => ({
      items: [],
      add: (item) =>
        set((s) =>
          s.items.some((candidate) => candidate.id === item.id)
            ? s
            : { items: [...s.items, transitionUploadTask(item, {})] },
        ),
      patch: (id, patch) => {
        const current = get().items;
        const items = applyUploadTaskPatches(current, [{ id, patch }]);
        if (items !== current) set({ items });
      },
      patchMany: (patches) => {
        const current = get().items;
        const items = applyUploadTaskPatches(current, patches);
        if (items !== current) set({ items });
      },
      remove: (id) => {
        uploadRuntimeClient.releaseFile(id);
        if (activatedUid) void uploadFileHandleStore.remove(activatedUid, id);
        set((s) => ({ items: s.items.filter((item) => item.id !== id) }));
      },
      clearCompleted: () =>
        set((s) => ({ items: s.items.filter((item) => !isTerminalPhase(item.phase)) })),
    }),
    {
      name: PERSIST_KEY,
      // Serialize ONLY plain, serializable metadata — never a `File`/`XMLHttpRequest` (they are not
      // JSON-serializable and live in the engine's in-memory map). `needsReattach` is deliberately NOT
      // persisted; it is a derived flag set fresh on rehydrate.
      partialize: (state) => ({ items: serializePersistedUploadTasks(state.items) }),
      storage: createJSONStorage(() => uidStorage),
      version: PERSIST_VERSION,
      migrate: (persistedState) => ({ items: migratedItemsFromUnknown(persistedState) }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        items: persistedItemsFromUnknown(persistedState),
      }),
      skipHydration: true,
    },
  ),
);

/**
 * Compute the rehydrated queue. Persisted **upload** phases that still need local bytes have lost
 * their in-memory `File` across the reload and are immediately flagged `needsReattach`. Completing
 * and processing uploads stay attached to their backend lifecycle and are reconciled without a file.
 * Imports are server-side. Ordinary terminal rows are omitted from persistence; an actionable MCAP
 * Recheck is the only terminal exception. Pure — exported for unit testing.
 */
export function markNeedsReattach(items: UploadTask[]): UploadTask[] {
  return items.map((i) => {
    const staleUpload =
      i.channel === 'upload' &&
      !i.statusSyncError &&
      (FILE_DEPENDENT_UPLOAD_PHASES.has(i.phase) || i.phase === 'error');
    return staleUpload ? { ...i, needsReattach: true } : i;
  });
}

async function hydrateLeaderQueue(
  uid: string,
  currentGeneration: number,
  reconcileAfterHydrate: boolean,
): Promise<void> {
  if (
    activatedUid !== uid ||
    currentGeneration !== activationGeneration ||
    !isLocalUploadLeader()
  ) {
    return;
  }
  await runtimeResetPromise;
  if (
    activatedUid !== uid ||
    currentGeneration !== activationGeneration ||
    !isLocalUploadLeader()
  ) {
    return;
  }
  // Capture the SharedWorker view before clearing this tab's follower projection. It is the most
  // recent in-memory state published by the previous leader and may be slightly newer than the
  // synchronous localStorage checkpoint.
  const sharedSnapshot = sharedRuntimeAvailable
    ? await uploadRuntimeClient.requestSnapshot()
    : uploadRuntimeClient.latestSnapshot();
  if (
    activatedUid !== uid ||
    currentGeneration !== activationGeneration ||
    !isLocalUploadLeader()
  ) {
    return;
  }
  const sharedItems = sharedSnapshot?.items ?? [];
  suppressRuntimePublish = true;
  try {
    storageUid = null;
    useUploadStore.setState({ items: [] });
    storageUid = uid;
    await useUploadStore.persist.rehydrate();
    if (
      activatedUid !== uid ||
      currentGeneration !== activationGeneration ||
      !isLocalUploadLeader()
    ) {
      storageUid = null;
      useUploadStore.setState({ items: [] });
      return;
    }
    useUploadStore.setState((state) => ({
      items: markNeedsReattach(sharedItems.length > 0 ? sharedItems : state.items),
    }));
  } finally {
    suppressRuntimePublish = false;
  }
  uploadRuntimeClient.publishSnapshot(uploadActions.all());
  setUploadTabHasActiveUploads(
    uploadActions.all().some((item) => item.channel === 'upload' && !isTerminalPhase(item.phase)),
  );
  if (reconcileAfterHydrate) {
    const { reconcileUploads, reattachFile } = await import('../services/uploadEngine');
    if (isLocalUploadLeader() && currentGeneration === activationGeneration) {
      await reconcileUploads();
    }
    if (!isLocalUploadLeader() || currentGeneration !== activationGeneration) return;
    try {
      const { recoverPendingGcsFolderBatch } = await import('../services/gcsFolderBatch');
      await recoverPendingGcsFolderBatch(uid);
    } catch (error) {
      console.warn('[uploads] pending GCS folder batch recovery failed', error);
    }
    if (!isLocalUploadLeader() || currentGeneration !== activationGeneration) return;
    const candidates = uploadActions
      .all()
      .filter((item) => item.channel === 'upload' && item.needsReattach === true)
      .map((item) => item.id);
    const retainedFiles = await uploadRuntimeClient.requestFiles(candidates);
    for (const id of candidates) {
      if (!isLocalUploadLeader() || currentGeneration !== activationGeneration) return;
      const item = uploadActions.get(id);
      const file =
        retainedFiles.get(id) ??
        (item ? await uploadFileHandleStore.readFile(uid, item) : undefined);
      if (file) await reattachFile(id, file, { resume: item?.phase !== 'paused' });
    }
  }
}

/**
 * Activate the signed-in user's live view, coordination and recovery. Persisted byte phases are
 * pessimistically marked interrupted, then automatically reattached when the SharedWorker or an
 * authorized FileSystemFileHandle can still provide the original File.
 */
export async function activateUploadsForUser(uid: string): Promise<void> {
  if (activatedUid === uid) return;
  deactivateUploads();
  activatedUid = uid;
  const currentGeneration = ++activationGeneration;
  let startingCoordination = true;
  useUploadStore.setState({ items: [] });
  sharedRuntimeAvailable = uploadRuntimeClient.connect(uid, {
    onSnapshot: (snapshot) => {
      if (
        activatedUid !== uid ||
        currentGeneration !== activationGeneration ||
        isLocalUploadLeader()
      ) {
        return;
      }
      useUploadStore.setState({ items: snapshot.items });
      uploadProgressActions.replace(snapshot.progressByUploadTaskId);
    },
    onProgress: (progressByUploadTaskId) => {
      if (
        activatedUid === uid &&
        currentGeneration === activationGeneration &&
        !isLocalUploadLeader()
      ) {
        uploadProgressActions.replace(progressByUploadTaskId);
      }
    },
  });
  unsubscribeLeadership = useUploadLeadershipStore.subscribe((state, previous) => {
    if (
      (state.uid !== uid && previous.uid !== uid) ||
      state.status === previous.status ||
      currentGeneration !== activationGeneration
    ) {
      return;
    }
    uploadRuntimeClient.setLeader(state.status === 'leader');
    if (state.status === 'leader') {
      if (!startingCoordination) void hydrateLeaderQueue(uid, currentGeneration, true);
    } else if (previous.status === 'leader') {
      storageUid = null;
      if (!sharedRuntimeAvailable) useUploadStore.setState({ items: [] });
      uploadPreparationActions.dismiss();
      const reset = import('../services/uploadEngine').then(({ resetUploadRuntime }) =>
        resetUploadRuntime(),
      );
      runtimeResetPromise = reset;
      void reset.finally(() => {
        if (runtimeResetPromise === reset) runtimeResetPromise = null;
      });
    }
  });
  await startUploadTabCoordination(uid);
  startingCoordination = false;
  if (isLocalUploadLeader()) await hydrateLeaderQueue(uid, currentGeneration, true);
}

export function getActiveUploadUid(): string | null {
  return activatedUid;
}

export function deactivateUploads(): void {
  activationGeneration += 1;
  unsubscribeLeadership?.();
  unsubscribeLeadership = null;
  uploadRuntimeClient.setLeader(false);
  uploadRuntimeClient.disconnect();
  sharedRuntimeAvailable = false;
  stopUploadTabCoordination();
  activatedUid = null;
  storageUid = null;
  useUploadStore.setState({ items: [] });
  uploadPreparationActions.dismiss();
}

/** Imperative (non-hook) accessors for the upload engine. */
export const uploadActions = {
  add: (item: UploadTask) => useUploadStore.getState().add(item),
  patch: (id: string, patch: Partial<UploadTask>) => useUploadStore.getState().patch(id, patch),
  patchMany: (patches: readonly UploadTaskPatch[]) => useUploadStore.getState().patchMany(patches),
  remove: (id: string) => useUploadStore.getState().remove(id),
  get: (id: string): UploadTask | undefined =>
    useUploadStore.getState().items.find((i) => i.id === id),
  all: (): UploadTask[] => useUploadStore.getState().items,
};

useUploadStore.subscribe((state) => {
  if (!isLocalUploadLeader() || suppressRuntimePublish) return;
  uploadRuntimeClient.publishSnapshot(state.items);
  setUploadTabHasActiveUploads(
    state.items.some((item) => item.channel === 'upload' && !isTerminalPhase(item.phase)),
  );
});

useUploadProgressStore.subscribe((state) => {
  if (isLocalUploadLeader()) uploadRuntimeClient.publishProgress(state.byUploadTaskId);
});
